import { InAppState } from "../base/InAppState";
import template from "./HomeTemplate.html";

import { Weather } from "../../core/Weather";
import { DragScroll } from "../../core/DragScroll";
import { _lcs } from "../../i18n";

// How many recommendation cards the rail shows before it hands over to the
// full "Empfehlungen" screen.
const RAIL_LIMIT = 5;

// "Schnellzugriff": how many Gästemappe modules, and the tile colours they
// cycle through.
const QUICK_ACTIONS = 4;
const QUICK_ACTION_TONES = [ "sand", "green", "sky", "green" ];

// Region key -> display label (platform-defined regions). German source
// strings double as their dictionary keys; enter() translates them.
const REGION_LABELS = {
    saechsische_schweiz : "Sächsische Schweiz"
};

/**
 * HomeState — the "Entdecken" tab: greeting, weather, the Gästemappe card,
 * quick actions, today's event and regional recommendations. The hub the guest
 * lands on after entering.
 *
 * Accommodation name/region, the Gästemappe card and the quick actions are fed
 * from the resolved accommodation + its guestbook blocks (see
 * App.resolveAccommodation). The weather card is live too, from
 * core/Weather.js — both it and the "Vorhersage" button open the forecast
 * sheet, and the event teaser is the next date from core/Events.js. Places
 * are still demo content until that feature lands.
 */
export class HomeState extends InAppState
{
    static ROUTE = "/";
    static ID = "home";

    getActiveTab() { return "home"; }

    async enter()
    {
        // The next event that is actually coming up — the feed is already
        // sorted (soonest first) and kept to the guest radius; one that has
        // already begun is skipped. Null when nothing is planned, in which
        // case the teaser is left out entirely.
        const today = this.app.events.getNextCard();
        const accommodation = this.app.accommodation;

        this.renderScreen( template, {
            name      : this._firstName(),
            shortName : accommodation?.name || "Felsenblick",
            region    : _lcs( REGION_LABELS[ accommodation?.region ]
                || accommodation?.region || "Sächsische Schweiz" ),
            welcomeImageUrl : accommodation?.theme?.welcomeImageUrl || null,
            actions   : this._quickActions(),
            today    : today,
            mappeSub : this._mappeSub(),
            weather  : this._weatherCard( this.app.weather.peek() )
        } );

        this.bind();

        // Deliberately not awaited: the screen must not wait on the network.
        // Renders above already show the cached reading, and _refreshWeather
        // patches the card in place once a fresh one arrives.
        this._refreshWeather();
        this._refreshRecommendations();

        // "Install the app" — on the home screen only, rarely (see core/Pwa).
        this.app.pwa.maybeShowInstallHint();
    }

    async exit()
    {
        this.app.pwa.hideInstallHint();
        await super.exit();
    }

    /* ----------------------------- Recommendations --------------------------- */

    /**
     * Fill the recommendation rail once the host's picks arrive. Same shape as
     * _refreshWeather: the screen is already on the glass, this patches a
     * section into it — and leaves that section hidden if there is nothing to
     * show, which is the common case for a house that has not curated yet.
     */
    async _refreshRecommendations()
    {
        await this.app.region.loadRecommendations();

        const section = this._q( '[data-id="recos"]' );
        const rail = this._q( '[data-id="recos-rail"]' );
        const allBtn = this._q( '[data-id="recos-all"]' );
        if ( !section || !rail ) return;

        const cards = this.app.region.recommendationCards();
        if ( cards.length === 0 )
        {
            section.hidden = true;
            return;
        }

        const shown = cards.slice( 0, RAIL_LIMIT );
        const hasMore = cards.length > RAIL_LIMIT;

        rail.innerHTML =
            shown.map( ( card ) => this._recoCardHtml( card ) ).join( "" ) +
            // The overflow card is the rail's last item rather than a link above
            // it: a guest who has scrolled to the end is exactly where the
            // "there is more" affordance belongs.
            ( hasMore ? this._recoMoreCardHtml( cards.length ) : "" );

        if ( allBtn ) allBtn.hidden = !hasMore;
        section.hidden = false;

        this._bindRail( rail );
    }

    _recoCardHtml( card )
    {
        const photo = card.image
            ? '<span class="placecard__photo ga-photo" style="background-image:url(' + this._cssUrl( card.image ) + ')"></span>'
            : '<span class="placecard__photo ga-photo ga-photo--' + this._esc( card.photo ) + '">' +
                  '<i class="icon icon--' + this._esc( card.icon ) + '"></i></span>';

        return '' +
            '<button class="placecard" type="button" data-entry="' + this._esc( card.kind ) + ':' + this._esc( card.id ) + '">' +
                photo +
                '<span class="placecard__body">' +
                    '<span class="badge badge--accent badge--sm">' + this._esc( card.cat ) + '</span>' +
                    '<span class="placecard__title">' + this._esc( card.title ) + '</span>' +
                    '<span class="placecard__meta">' + this._esc( card.distance || card.meta ) + '</span>' +
                '</span>' +
            '</button>';
    }

    _recoMoreCardHtml( total )
    {
        return '' +
            '<button class="placecard placecard--more" type="button" data-id="recos-more">' +
                '<span class="placecard--more__icon"><i class="icon icon--heart"></i></span>' +
                '<span class="placecard--more__title">' + this._esc( _lcs( "Alle Empfehlungen" ) ) + '</span>' +
                '<span class="placecard--more__meta">' +
                    this._esc( _lcs( "{count} Tipps deiner Gastgeber", { count : total, n : total } ) ) +
                '</span>' +
                '<span class="placecard--more__cta">' + this._esc( _lcs( "Ansehen" ) ) +
                    ' <i class="icon icon--arrow-right"></i></span>' +
            '</button>';
    }

    /** Card taps, plus making the rail reachable with a mouse. */
    _bindRail( rail )
    {
        this.addEventListener( rail, "click", ( e ) =>
        {
            if ( e.target.closest( '[data-id="recos-more"]' ) )
            {
                this.app.router.redirect( "/recommendations" );
                return;
            }

            const card = e.target.closest( "[data-entry]" );
            if ( !card ) return;

            const [ kind, id ] = String( card.getAttribute( "data-entry" ) ).split( ":" );
            if ( kind && id ) this.app.router.redirect( "/region/" + kind + "/" + id );
        } );

        // Press-and-drag to pan, the same gesture the other card rails use
        // (event gallery, region photos). `wheel: false` for the reason
        // DragScroll documents: this rail sits mid-way down a screen that
        // scrolls vertically, and claiming the wheel there would stop the page
        // dead every time the pointer crossed the strip of cards.
        DragScroll.attach( this, rail, { wheel : false } );
    }

    _cssUrl( url )
    {
        // Quoted and escaped: an image URL ends up inside a style attribute.
        return "'" + String( url ).replace( /['"\\\n\r]/g, "" ) + "'";
    }

    _esc( value )
    {
        return String( value === null || value === undefined ? "" : value )
            .replace( /&/g, "&amp;" )
            .replace( /</g, "&lt;" )
            .replace( />/g, "&gt;" )
            .replace( /"/g, "&quot;" );
    }

    /**
     * The weather statcard's three values. Falls back to a neutral placeholder
     * while the first forecast is still loading, and to an explicit note when
     * there is none to be had (no tenant resolved, or the API is unreachable
     * with nothing cached).
     */
    _weatherCard( forecast )
    {
        const shortName = this.app.accommodation?.name || "Felsenblick";

        if ( !forecast || !forecast.current )
        {
            return {
                icon : "cloud-sun",
                temp : "–",
                meta : this.app.weather.failed
                    ? _lcs( "Wetter nicht verfügbar" )
                    : _lcs( "Wetter wird geladen …" )
            };
        }

        const current = forecast.current;
        return {
            icon : Weather.icon( current.symbol ),
            temp : Weather.temp( current.temperature ),
            meta : Weather.label( current.symbol ) + " · " + shortName
        };
    }

    /**
     * Resolve the forecast and write it into the already-rendered card, rather
     * than re-rendering the screen (which would drop the guest's scroll
     * position and re-bind every handler). A no-op if they navigated away while
     * the request was in flight — exit() empties the content slot, so the
     * lookups below simply find nothing.
     */
    async _refreshWeather()
    {
        const forecast = await this.app.weather.load();

        const iconEl = this._q( '[data-id="weather-icon"]' );
        const tempEl = this._q( '[data-id="weather-temp"]' );
        const metaEl = this._q( '[data-id="weather-meta"]' );
        if ( !iconEl || !tempEl || !metaEl )
        {
            return;
        }

        const card = this._weatherCard( forecast );
        iconEl.className = "icon icon--" + card.icon + " statcard__icon statcard__icon--sand";
        tempEl.textContent = card.temp;
        metaEl.textContent = card.meta;

        // Nothing to open — let the weather card take the whole row instead of
        // offering a button that leads to an empty sheet. `is-hidden` (the
        // shell's convention) rather than the `hidden` attribute: .statcard sets
        // display:flex, which would win against the UA's [hidden] rule.
        const hasForecast = !!( forecast && forecast.current );
        this._q( '[data-id="weather-details"]' )?.classList.toggle( "is-hidden", !hasForecast );
        this._q( '[data-id="weather-card"]' )?.classList.toggle( "statcard--full", !hasForecast );
    }

    /**
     * Sub-line of the Gästemappe card: the first visible block titles, so the
     * guest sees what their host actually filled in.
     */
    _mappeSub()
    {
        const titles = this.app.getGuestbookSections()
            .flatMap( ( sec ) => sec.blocks.map( ( b ) => b.title ) );
        if ( titles.length === 0 )
        {
            return _lcs( "WLAN, Frühstück, Check-out & mehr" );
        }
        const shown = titles.slice( 0, 3 ).join( ", " );
        return titles.length > 3 ? _lcs( "{list} & mehr", { list : shown } ) : shown;
    }

    /**
     * "Schnellzugriff": QUICK_ACTIONS modules picked at random from what the
     * guest can actually see in the Gästemappe, so the tiles never point at
     * something the host has not filled in — and a returning guest gets a
     * different mix. Each tile opens the Gästemappe on that module (see
     * InfoState, `?block=`).
     */
    _quickActions()
    {
        const pool = this.app.getGuestbookSections().flatMap( ( sec ) =>
            sec.blocks
                .filter( ( b ) => b.id )
                .map( ( b ) => ( { block : b, section : sec } ) ) );

        // Fisher–Yates on a copy, then keep the first few.
        for ( let i = pool.length - 1; i > 0; i-- )
        {
            const j = Math.floor( Math.random() * ( i + 1 ) );
            [ pool[ i ], pool[ j ] ] = [ pool[ j ], pool[ i ] ];
        }

        return pool.slice( 0, QUICK_ACTIONS ).map( ( { block, section }, index ) => ( {
            blockId : block.id,
            icon    : block.icon || section.icon || "info",
            label   : block.title || section.title,
            sub     : ( block.summary || "" ).trim() || section.title,
            tone    : QUICK_ACTION_TONES[ index % QUICK_ACTION_TONES.length ]
        } ) );
    }

    bind()
    {
        const go = ( route ) => this.app.router.redirect( route );

        this.addEventListener( this._q( '[data-id="info"]' ),       "click", () => go( "/info" ) );

        // The next-event teaser is left out when the house has nothing planned.
        const allEvents = this._q( '[data-id="all-events"]' );
        const nextEvent = this._q( '[data-id="today"]' );
        if ( allEvents ) this.addEventListener( allEvents, "click", () => go( "/events" ) );
        if ( nextEvent ) this.addEventListener( nextEvent, "click", () => go( "/events" ) );

        // Both weather cards open the same sheet, on the view that matches what
        // was tapped: the current reading leads to the hour-by-hour rail, the
        // "Vorhersage" button to the 7-day outlook and detail metrics.
        const openWeather = ( mode ) => () =>
        {
            if ( this.app.weather.peek() )
            {
                return this.app.view.openWeather( mode );
            }
            this.app.view.toast( _lcs( "Das Wetter ist gerade nicht verfügbar." ), "info" );
        };
        this.addEventListener( this._q( '[data-id="weather-card"]' ),    "click", openWeather( "hours" ) );
        this.addEventListener( this._q( '[data-id="weather-details"]' ), "click", openWeather( "forecast" ) );

        this._qa( ".qa" ).forEach( ( btn ) =>
        {
            this.addEventListener( btn, "click", () =>
                go( "/info?block=" + encodeURIComponent( btn.getAttribute( "data-block" ) ) ) );
        } );

        // The recommendation rail is filled after the fetch and binds its own
        // cards there (see _bindRail); only its heading link exists this early.
        const allRecos = this._q( '[data-id="recos-all"]' );
        if ( allRecos ) this.addEventListener( allRecos, "click", () => go( "/recommendations" ) );
    }

    /**
     * The first name for the greeting, or null — the greeting then drops the
     * name rather than inventing one. Read from the account's own name field:
     * app.user.name falls back to the e-mail address (and to "Gast" for
     * "Als Gast fortfahren"), and neither belongs after "Hallo".
     */
    _firstName()
    {
        if ( this.app.user?.guest )
        {
            return null;
        }
        const name = String( this.app.session?.user?.data?.name || "" ).trim();
        return name ? name.split( /\s+/ )[ 0 ] : null;
    }

    _q( sel )  { return this.app.view.pageContent.querySelector( sel ); }
    _qa( sel ) { return this.app.view.pageContent.querySelectorAll( sel ); }
}
